"use client";

import Link from "next/link";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Logo from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";
import { useAuthSync } from "@/hooks/use-auth-sync";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowRight, ChevronDown, LogOut, User } from "lucide-react";

const APP_URL = process.env.NEXT_PUBLIC_APP_URL || "https://communo.app";

const Navbar = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, isLoading, logout } = useAuthSync();

  return (
    <nav
      aria-label={t("nav.main")}
      className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border"
    >
      <div className="container mx-auto flex items-center justify-between h-16 px-6">
        <Link href="/" className="flex items-center gap-2">
          <Logo className="h-9 w-auto" />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link href="/#features" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">{t("nav.features")}</Link>
          <Link href="/#network" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">{t("nav.forWho")}</Link>
          <Link href="/#ethics" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">{t("nav.openSource")}</Link>
          <Link href="/#pricing" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">{t("nav.pricing")}</Link>
          <Link href="/blog" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">{t("nav.blog")}</Link>
        </div>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-muted animate-pulse" />
              <div className="hidden sm:block h-4 w-24 rounded bg-muted animate-pulse" />
            </div>
          ) : isAuthenticated && user ? (
            <>
              {/* Identity chip — dropdown with profile/logout */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    aria-label={t("nav.accountMenu", { name: user.fullname })}
                    className="flex items-center gap-2 rounded-xl px-3 py-1.5 hover:bg-muted transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <Avatar className="h-7 w-7 shrink-0">
                      <AvatarImage src={user.avatar?.contentUrl} alt={user.fullname} />
                      <AvatarFallback className="text-xs font-semibold">
                        {user.fullname.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:block text-sm font-medium max-w-[140px] truncate">
                      {user.fullname}
                    </span>
                    <ChevronDown
                      aria-hidden="true"
                      className="hidden sm:block h-3.5 w-3.5 text-muted-foreground shrink-0"
                    />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-52" align="end" forceMount>
                  <div className="flex items-center gap-3 px-3 py-2.5">
                    <Avatar className="h-9 w-9 shrink-0">
                      <AvatarImage src={user.avatar?.contentUrl} alt={user.fullname} />
                      <AvatarFallback className="text-sm font-semibold">
                        {user.fullname.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="text-sm font-medium leading-none truncate">{user.fullname}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t("nav.member")}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <a href={`${APP_URL}/my/profile`} className="flex items-center w-full cursor-pointer">
                      <User className="mr-2 h-4 w-4" aria-hidden="true" />
                      <span>{t("nav.myProfile")}</span>
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => logout()}
                    className="text-muted-foreground focus:text-foreground cursor-pointer"
                  >
                    <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                    <span>{t("nav.logout")}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Primary CTA */}
              <Button variant="hero" size="sm" asChild>
                <a href={APP_URL} className="flex items-center gap-1.5">
                  <span>{t("nav.goToApp")}</span>
                  <ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />
                </a>
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" className="hidden sm:inline-flex" asChild>
                <a href={process.env.NEXT_PUBLIC_LOGIN_URL}>{t("nav.login")}</a>
              </Button>
              <Button variant="hero" size="sm" asChild>
                <a href={process.env.NEXT_PUBLIC_REGISTER_URL}>{t("nav.start")}</a>
              </Button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
